/**
 * Size guide 2.0
 *
 * One piece of state — the wall width in centimetres — drives everything: the slider, the size buttons, the result
 * text, the image and the badge. The slider moves freely; the size blocks are checkpoints on it. Clicking a size
 * simply moves the slider to that checkpoint, which is why the two can never disagree.
 *
 * Widths are always held in centimetres. Inches are a display conversion.
 */
(function () {
  const CM_PER_INCH = 2.54;

  function clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  class SizeGuide extends HTMLElement {
    connectedCallback() {
      this.range = this.querySelector('[data-size-guide-range]');
      if (!this.range || this.dataset.ready === 'true') return;
      this.dataset.ready = 'true';

      this.bubble = this.querySelector('[data-size-guide-bubble]');
      this.maxLabel = this.querySelector('[data-size-guide-max]');
      this.resultText = this.querySelector('[data-size-guide-result]');
      this.badge = this.querySelector('[data-size-guide-badge]');

      this.ticks = Array.from(this.querySelectorAll('[data-size-guide-tick]'));
      this.mediaItems = Array.from(this.querySelectorAll('[data-size-guide-media]'));
      this.sizeButtons = Array.from(this.querySelectorAll('[data-size-guide-size]'));
      this.unitButtons = Array.from(this.querySelectorAll('[data-size-guide-unit]'));

      this.min = Number(this.range.min) || 0;
      this.max = Number(this.range.max) || 100;
      this.unit = this.getAttribute('unit') === 'inch' ? 'inch' : 'cm';

      this.sizes = this.sizeButtons
        .map(function (button) {
          return {
            index: Number(button.dataset.index),
            value: Number(button.dataset.value) || 0,
            label: button.dataset.label || '',
            dimensions: {
              cm: button.dataset.dimensionsCm || '',
              inch: button.dataset.dimensionsInch || '',
            },
          };
        })
        .sort(function (a, b) {
          return a.value - b.value;
        });

      this.range.addEventListener('input', this._onRangeInput.bind(this));

      this.sizeButtons.forEach(
        function (button) {
          button.addEventListener('click', this._onSizeClick.bind(this, button));
        }.bind(this)
      );

      this.unitButtons.forEach(
        function (button) {
          button.addEventListener('click', this._onUnitClick.bind(this, button));
        }.bind(this)
      );

      /* The bubble is positioned in pixels, so it has to follow the track's width. */
      if ('ResizeObserver' in window) {
        this.resizeObserver = new ResizeObserver(this._positionBubble.bind(this));
        this.resizeObserver.observe(this.range);
      } else {
        window.addEventListener('resize', this._positionBubble.bind(this));
      }

      this.classList.add('is-ready');
      this.setValue(Number(this.getAttribute('value')) || this.min, { force: true });
      this.setUnit(this.unit);
    }

    disconnectedCallback() {
      if (this.resizeObserver) this.resizeObserver.disconnect();
    }

    /* ---------- Formatting ---------- */

    toDisplay(centimetres) {
      return this.unit === 'inch' ? Math.round(centimetres / CM_PER_INCH) : Math.round(centimetres);
    }

    get suffix() {
      return (this.unit === 'inch' ? this.getAttribute('inch-suffix') : this.getAttribute('cm-suffix')) || '';
    }

    format(centimetres) {
      return this.toDisplay(centimetres) + this.suffix;
    }

    /* ---------- State ---------- */

    /** The recommended size is the largest one whose checkpoint the wall width has reached. */
    sizeFor(centimetres) {
      let match = this.sizes[0];

      this.sizes.forEach(function (size) {
        if (centimetres >= size.value) match = size;
      });

      return match;
    }

    setValue(centimetres, options) {
      const value = clamp(Math.round(centimetres), this.min, this.max);
      if (!(options && options.force) && value === this.value) return;

      this.value = value;
      if (Number(this.range.value) !== value) this.range.value = String(value);

      this._renderSlider();
      this._renderSelection(this.sizeFor(value));
    }

    setUnit(unit) {
      this.unit = unit === 'inch' ? 'inch' : 'cm';
      this.setAttribute('unit', this.unit);

      this.unitButtons.forEach(
        function (button) {
          button.setAttribute('aria-pressed', String(button.dataset.unit === this.unit));
        }.bind(this)
      );

      this._renderSlider();
      this._renderSelection(this.sizeFor(this.value));
    }

    /* ---------- Rendering ---------- */

    _renderSlider() {
      const ratio = this.max === this.min ? 0 : (this.value - this.min) / (this.max - this.min);

      this.range.style.setProperty('--size-guide-progress', (ratio * 100).toFixed(2) + '%');
      this.range.setAttribute('aria-valuetext', this.format(this.value));

      if (this.bubble) this.bubble.textContent = this.format(this.value);
      if (this.maxLabel) this.maxLabel.textContent = this.format(this.max);

      this._positionBubble();

      this.ticks.forEach(
        function (tick) {
          const tickValue = Number(tick.dataset.value) || 0;
          const tickRatio = this.max === this.min ? 0 : (tickValue - this.min) / (this.max - this.min);

          tick.style.insetInlineStart = (tickRatio * 100).toFixed(2) + '%';
          tick.classList.toggle('is-reached', this.value >= tickValue);
        }.bind(this)
      );
    }

    _positionBubble() {
      if (!this.bubble) return;

      const width = this.range.offsetWidth;
      if (!width) return;

      /* The thumb's centre never reaches the very ends of the track, so the travel is inset by half a thumb. */
      const styles = getComputedStyle(this);
      const thumb = parseFloat(styles.getPropertyValue('--size-guide-thumb-size')) || 22;
      const ratio = this.max === this.min ? 0 : (this.value - this.min) / (this.max - this.min);

      this.bubble.style.left = (thumb / 2 + ratio * (width - thumb)).toFixed(1) + 'px';
    }

    _renderSelection(size) {
      if (!size) return;

      /* Each size button carries its own dimensions, shown in the current unit. */
      this.sizeButtons.forEach(
        function (button) {
          button.setAttribute('aria-pressed', String(Number(button.dataset.index) === size.index));

          const dimensions = button.querySelector('[data-size-guide-size-dimensions]');
          if (dimensions) {
            const cm = button.dataset.dimensionsCm || '';
            const inch = button.dataset.dimensionsInch || '';
            dimensions.textContent = (this.unit === 'inch' ? inch || cm : cm || inch);
          }
        }.bind(this)
      );

      this.ticks.forEach(
        function (tick, index) {
          tick.classList.toggle('is-active', index === size.index);
        }.bind(this)
      );

      this.mediaItems.forEach(function (item) {
        item.classList.toggle('is-active', Number(item.dataset.index) === size.index);
      });

      if (this.badge) this.badge.textContent = size.label;

      if (this.resultText) {
        const template = this.getAttribute('result-template') || '';
        this.resultText.textContent = template
          .replace('[width]', this.format(size.value))
          .replace('[size]', size.label);
      }
    }

    /* ---------- Events ---------- */

    _onRangeInput() {
      this.setValue(Number(this.range.value));
    }

    _onSizeClick(button) {
      this.setValue(Number(button.dataset.value) || this.min, { force: true });
    }

    _onUnitClick(button) {
      this.setUnit(button.dataset.unit);
    }
  }

  if (!window.customElements.get('size-guide')) {
    window.customElements.define('size-guide', SizeGuide);
  }
})();
